/**
 * JWT-Storage + Auth-State (Zustand-Store).
 *
 * Architektur-Entscheidung: Token in localStorage (nicht httpOnly Cookie).
 *   - Begründung: Lokales Single-User-Tool, keine XSS-Surface (kein User-generated
 *     HTML, keine Drittanbieter-Iframes), CSRF irrelevant ohne Cookie-Auth.
 *   - In einem produktiven Cloud-Setup wäre httpOnly + SameSite=strict Pflicht.
 *   - Siehe README → Tech-Debt.
 */

import { create } from 'zustand';

const STORAGE_KEY = 'wartungstool.token';
const EXPIRY_KEY = 'wartungstool.token.expires_at';

export interface AuthState {
  token: string | null;
  expiresAt: number | null; // epoch ms
  isAuthenticated: boolean;
  setToken: (token: string, expiresInSeconds: number) => void;
  clear: () => void;
  hydrate: () => void;
}

function readPersisted(): { token: string | null; expiresAt: number | null } {
  if (typeof window === 'undefined') return { token: null, expiresAt: null };
  try {
    const token = window.localStorage.getItem(STORAGE_KEY);
    const expRaw = window.localStorage.getItem(EXPIRY_KEY);
    const expiresAt = expRaw ? Number.parseInt(expRaw, 10) : null;
    if (!token || !expiresAt) return { token: null, expiresAt: null };
    if (Date.now() >= expiresAt) {
      // Abgelaufen → still aufräumen.
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem(EXPIRY_KEY);
      return { token: null, expiresAt: null };
    }
    return { token, expiresAt };
  } catch {
    return { token: null, expiresAt: null };
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  token: null,
  expiresAt: null,
  isAuthenticated: false,
  setToken: (token, expiresInSeconds) => {
    const expiresAt = Date.now() + expiresInSeconds * 1000;
    try {
      window.localStorage.setItem(STORAGE_KEY, token);
      window.localStorage.setItem(EXPIRY_KEY, String(expiresAt));
    } catch {
      // Storage voll oder verboten – State bleibt im Memory.
    }
    set({ token, expiresAt, isAuthenticated: true });
  },
  clear: () => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem(EXPIRY_KEY);
    } catch {
      /* ignore */
    }
    set({ token: null, expiresAt: null, isAuthenticated: false });
  },
  hydrate: () => {
    const { token, expiresAt } = readPersisted();
    set({ token, expiresAt, isAuthenticated: token !== null });
  },
}));

/**
 * Direkter, sychroner Token-Zugriff für den fetch-Wrapper
 * (vermeidet Hook-Aufrufe außerhalb von Komponenten).
 */
export function getAccessToken(): string | null {
  const state = useAuthStore.getState();
  if (state.token && state.expiresAt && Date.now() < state.expiresAt) {
    return state.token;
  }
  return null;
}

/** Wird vom api-Wrapper bei 401-Antworten aufgerufen. */
export function forceLogout(): void {
  useAuthStore.getState().clear();
}
